'use client'

import '../globals.css'
import { useEffect } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { ContentSection } from 'sidecar-components/atoms/ContentSection'
import { Icon, IconSize } from 'sidecar-components/atoms/Icon'
import { Button, ButtonSize } from 'sidecar-components/atoms/Button'
import { logger } from '@/lib/services/logger.service'

/**
 * Global error boundary for the group portal.
 *
 * Catches errors in the root layout (rare but critical failures). This is the
 * last line of defense for unhandled errors and must include its own HTML
 * structure since it cannot rely on any layout components.
 *
 * Note: This boundary only catches errors in production. In development, the
 * Next.js error overlay will be shown instead.
 */

interface GlobalErrorProps {
  /** Error object with optional digest for tracking */
  error: Error & { digest?: string }
  /** Function to attempt recovering by re-rendering */
  reset: () => void
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    logger.error('Global Error', error, {
      source: 'nextjs-global-error',
      digest: error.digest,
    })
    try {
      datadogRum.addError(error, {
        source: 'nextjs-global-error',
        digest: error.digest,
      })
    } catch {
      // RUM may not be initialized; logger.error already captured the event
    }
  }, [error])

  return (
    <html lang="en">
      <head>
        <title>Service Unavailable - Group Portal</title>
        <meta
          name="description"
          content="We're experiencing technical difficulties"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" sizes="any" />
      </head>
      <body className="antialiased">
        <ContentSection breathable className="text-center">
          <Icon
            name="sidecar"
            className="mx-auto color-iconBrand-default"
            size={IconSize.XXXXL}
          />

          <h1 className="my-12">
            We encountered a critical error. Please reload the page or contact
            support if the problem persists.
          </h1>

          {/* Error digest for support reference */}
          {error.digest && (
            <p className="text-sm color-text-weak mb-4">
              Error ID: {error.digest}
            </p>
          )}

          <div className="flex gap-4 justify-center">
            {/* Reload button - attempts recovery */}
            <Button
              size={ButtonSize.LARGE}
              icon="refresh"
              onClick={reset}
              testId="btn_reload"
            >
              Reload
            </Button>

            {/* Return to login - safe fallback for authenticated errors */}
            <Button
              size={ButtonSize.LARGE}
              icon="forward"
              onClick={() => (window.location.href = '/login')}
              testId="btn_returnToLogin"
            >
              Return to login
            </Button>
          </div>
        </ContentSection>
      </body>
    </html>
  )
}
